import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Clock3, Eye, EyeOff, KeyRound, Laptop, Lock, LogOut, Pencil, ShieldCheck, UserRound, Users, X } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../api/client';
import { useSession } from '../app/session';
import { ask } from '../components/Confirm';
import { ErrorBox, PageHeader, timeAgo } from '../components/ui';
import { AccountTabs } from './UsersPage';

const ROLE: Record<string, [string, string]> = {
  OWNER: ['Owner', 'Everything, including users and billing'],
  ADMIN: ['Admin', 'All settings and content'],
  OPERATOR: ['Operator', 'Day-to-day content, guests and messages'],
  VIEWER: ['Viewer', 'Read only'],
};

/** 0–5 from length and character mix. */
function strength(p: string) {
  if (!p) return 0;
  let s = p.length >= 10 ? 1 : 0;
  if (p.length >= 14) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return Math.min(5, s);
}
const STRENGTH = ['', 'Weak', 'Weak', 'Fair', 'Good', 'Strong'];

/** Browser and OS from the user agent, e.g. "Windows · Edge". */
function thisBrowser() {
  const ua = navigator.userAgent;
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : 'Unknown';
  const br = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  return `${os} · ${br}`;
}

function Tile({ icon, tone, title, value, sub, aside }: { icon: ReactNode; tone: string; title: string; value: string; sub: string; aside?: ReactNode }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="stat-tile-label" style={{ color: 'var(--ink)', fontWeight: 700 }}>
          {title}
        </span>
        <span style={{ display: 'block', fontWeight: 700, fontSize: 15, marginTop: 2 }}>{value}</span>
        <span className="stat-tile-sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sub}
        </span>
      </span>
      {aside}
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, auto }: { value: string; onChange: (v: string) => void; placeholder: string; auto: string }) {
  const [show, setShow] = useState(false);
  return (
    <span className="pw-input">
      <input type={show ? 'text' : 'password'} autoComplete={auto} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      <button type="button" onClick={() => setShow(!show)} title={show ? 'Hide' : 'Show'} tabIndex={-1}>
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </span>
  );
}

/** Account settings: profile, password, sessions, sign out. */
export function AccountPage() {
  const { me, sites, logout } = useSession();
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);
  const [name, setName] = useState<string | null>(null);
  const [othersOut, setOthersOut] = useState(false);
  const change = useMutation({
    mutationFn: () => api.post('/auth/password', { current, next }),
    onSuccess: async () => {
      setDone(true);
      setCurrent('');
      setNext('');
      setRepeat('');
      await qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const rename = useMutation({
    mutationFn: (n: string) => api.patch('/auth/profile', { name: n }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] });
      setName(null);
    },
  });
  const signOutOthers = useMutation({ mutationFn: () => api.post('/auth/sign-out-others'), onSuccess: () => setOthersOut(true) });
  const mismatch = repeat.length > 0 && next !== repeat;
  const tooShort = next.length > 0 && next.length < 10;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setDone(false);
    if (!mismatch && next.length >= 10 && current) change.mutate();
  };
  const st = strength(next);
  const [roleName, roleDesc] = ROLE[me.role] ?? [me.role, ''];
  const initial = (me.user.name || me.user.email).trim()[0]?.toUpperCase() ?? '?';
  const signedIn = me.user.lastLoginAt;
  const pwd = me.user.passwordChangedAt;

  return (
    <>
      <PageHeader title="Account settings" sub="Your profile, security and sign-in." />
      <AccountTabs />
      <div className="stat-strip">
        <Tile icon={<UserRound size={22} />} tone="violet" title="Profile" value={me.user.name || '—'} sub={me.user.email} />
        <Tile
          icon={<ShieldCheck size={22} />}
          tone="green"
          title="Security"
          value="Password set"
          sub={pwd ? `Last changed ${timeAgo(pwd)}` : 'Never changed here'}
          aside={<span className="badge ok">Secure</span>}
        />
        <Tile icon={<Users size={22} />} tone="violet" title="Role" value={roleName} sub={roleDesc} />
        <Tile
          icon={<Clock3 size={22} />}
          tone="blue"
          title="Last sign-in"
          value={signedIn ? new Date(signedIn).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
          sub={signedIn ? timeAgo(signedIn) : ''}
        />
      </div>

      <div className="account-grid">
        <div className="card">
          <div className="pkg-head">
            <span className="pkg-head-icon">
              <UserRound size={22} />
            </span>
            <span style={{ flex: 1 }}>
              <span className="pkg-head-title">Profile information</span>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                Your account details and organization.
              </span>
            </span>
            {name === null && (
              <button className="btn" onClick={() => setName(me.user.name ?? '')}>
                <Pencil size={15} /> Edit profile
              </button>
            )}
          </div>
          <div className="profile-body">
            <span className="profile-avatar">{initial}</span>
            <dl className="profile-kv">
              <dt>Full name</dt>
              <dd>
                {name === null ? (
                  me.user.name || '—'
                ) : (
                  <span className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                    <input value={name} maxLength={80} autoFocus onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name.trim() && rename.mutate(name.trim())} />
                    <button className="icon-btn" title="Save" disabled={!name.trim() || rename.isPending} onClick={() => rename.mutate(name.trim())}>
                      <Check size={16} />
                    </button>
                    <button className="icon-btn" title="Cancel" onClick={() => setName(null)}>
                      <X size={16} />
                    </button>
                  </span>
                )}
              </dd>
              <dt>Email address</dt>
              <dd>{me.user.email}</dd>
              <dt>Organization</dt>
              <dd>{me.organization.name}</dd>
              <dt>Role</dt>
              <dd>
                {roleName}: {roleDesc.toLowerCase()}
              </dd>
              <dt>Site access</dt>
              <dd>{me.siteIds.length ? sites.map((s) => s.name).join(', ') : `All sites (${sites.map((s) => s.name).join(', ') || '—'})`}</dd>
              <dt>Time zone</dt>
              <dd>{me.organization.timezone}</dd>
            </dl>
          </div>
          <ErrorBox error={rename.error} />
          <div className="profile-foot">
            <button type="button" className="btn danger-outline" onClick={() => void logout()}>
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        <form className="card" onSubmit={submit}>
          <div className="pkg-head">
            <span className="pkg-head-icon">
              <Lock size={22} />
            </span>
            <span>
              <span className="pkg-head-title">Change password</span>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                Other browsers signed in to your account are signed out. This one stays signed in.
              </span>
            </span>
          </div>
          <div className="pw-form">
            <label>
              <span className="camp-label">Current password</span>
              <PasswordInput value={current} onChange={setCurrent} placeholder="Enter your current password" auto="current-password" />
            </label>
            <label>
              <span className="camp-label">New password</span>
              <PasswordInput value={next} onChange={setNext} placeholder="Enter a new password" auto="new-password" />
              <span className={`camp-hint${tooShort ? ' err' : ''}`}>At least 10 characters. A mix of letters, numbers and symbols is stronger.</span>
              <span className="pw-meter">
                {[1, 2, 3, 4, 5].map((i) => (
                  <span key={i} className={i <= st ? `on s${st}` : ''} />
                ))}
                <em>{next ? STRENGTH[st] : 'Password strength'}</em>
              </span>
            </label>
            <label>
              <span className="camp-label">Confirm new password</span>
              <PasswordInput value={repeat} onChange={setRepeat} placeholder="Confirm your new password" auto="new-password" />
              {mismatch && <span className="camp-hint err">The passwords don't match.</span>}
            </label>
            {change.error && <ErrorBox error={change.error} />}
            {done && (
              <span className="badge ok" style={{ alignSelf: 'flex-start' }}>
                <Check size={12} /> Password changed
              </span>
            )}
            <button className="btn primary" style={{ justifyContent: 'center' }} disabled={change.isPending || mismatch || next.length < 10 || !current || !repeat}>
              <KeyRound size={16} /> {change.isPending ? 'Saving…' : 'Update password'}
            </button>
          </div>
        </form>

        <div className="card account-wide">
          <div className="pkg-head">
            <span className="pkg-head-icon">
              <Laptop size={22} />
            </span>
            <span style={{ flex: 1 }}>
              <span className="pkg-head-title">Sessions</span>
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                Where your account is signed in.
              </span>
            </span>
            <button
              className="btn danger-outline"
              disabled={signOutOthers.isPending}
              onClick={() =>
                ask('Sign out everywhere else? Every other browser and device signed in to your account is signed out. This one stays signed in.', () => signOutOthers.mutate(), {
                  action: 'Sign out others',
                  danger: true,
                })
              }
            >
              <LogOut size={15} /> Sign out other sessions
            </button>
          </div>
          <div className="session-row">
            <span className="session-ic">
              <Laptop size={22} />
            </span>
            <span style={{ flex: 1 }}>
              <span className="row" style={{ gap: 8 }}>
                <strong>{thisBrowser()}</strong>
                <span className="badge brand">This browser</span>
              </span>
              <span className="muted" style={{ fontSize: 13 }}>
                Signed in {signedIn ? new Date(signedIn).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : ''}
              </span>
            </span>
            {othersOut && (
              <span className="badge ok">
                <Check size={12} /> Other sessions signed out
              </span>
            )}
          </div>
          <ErrorBox error={signOutOthers.error} />
        </div>
      </div>
    </>
  );
}
